<template>
  <div class="container books-lister">
    <div class="row">
      <DesktopFilters
        :filters="filters"
        :selected-filter="selectedFilter"
        :on-filter-click="onFilterClick"
      />

      <div class="col-12 col-lg-9">
        <DesktopSorting
          :number-of-books="pagination.totalAmountOfBooks"
          :selected-value="sortBy"
          :on-sort-change="onSortChange"
        />

        <MobileFilterAndSort
          :number-of-books="pagination.totalAmountOfBooks"
          :selected-value="sortBy"
          :on-sort-change="onSortChange"
          :selected-filter="selectedFilter"
          :filters="filters"
          :on-filter-click="onFilterClick"
        />

        <div :class="['books-lister__list', { loading: isLoading }]">
          <div
            :key="`${sortBy}-${selectedFilter}-${currentPage}`"
            class="row mt-40"
            itemscope
            itemtype="http://schema.org/ItemList"
          >
            <div
              v-for="(book, index) in books"
              :ref="book.slug"
              :key="book.title"
              class="col-md-4 book mb-32"
              itemscope
              itemprop="itemListElement"
              itemtype="http://schema.org/ListItem"
            >
              <span class="d-none" itemprop="position" :content="index" />
              <NuxtLink
                :to="{
                  path: `/shop/boek/${book.slug}`,
                  query: routeQuery
                }"
                :data-testid="`shop-book${index}`"
                itemprop="url"
                class="book__cover__anchor"
                @click.enter="pushSelectItemEvent(book)"
              >
                <div class="book__cover__wrapper mb-md-16">
                  <BookCover
                    :cover="book.cover"
                    :title="book.title"
                    :genres="book.genres"
                  />
                </div>
              </NuxtLink>

              <div>
                <NuxtLink
                  :to="{
                    path: `/shop/boek/${book.slug}`,
                    query: routeQuery
                  }"
                  class="reset-link-styling"
                  @click.enter="pushSelectItemEvent(book)"
                >
                  <div v-if="book.averageRating" class="mb-8">
                    <StarsRating :rating="book.averageRating" />
                  </div>

                  <h4 class="book__title">
                    {{ book.title }}
                  </h4>

                  <span
                    class="d-block mb-8 fw-semibold"
                    data-testid="book-author"
                  >
                    {{ book.authorName }}
                  </span>

                  <small v-if="book.genres" class="book__genre text-small">
                    {{ book.genres.join(', ') }}
                  </small>
                </NuxtLink>

                <div class="mt-8">
                  <BasePrice
                    :original-price="book.listPrice"
                    :discount-price="book.salePrice"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="pagination.totalNumberOfPages > 1"
          class="d-flex justify-content-center mt-32 mb-32"
        >
          <Pagination
            :total-pages="totalPages"
            :current-page="currentPage"
            @page-changed="pageChanged"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useRouteStore } from '~/stores/route'
import BasePrice from '../BasePrice'
import DesktopFilters from './DesktopFilters'
import DesktopSorting from './DesktopSorting'
import MobileFilterAndSort from './MobileFilterAndSort'
import BookCover from '~/components/books/BookCover'
import StarsRating from '~/components/reviews/StarsRating'
import { TRACKING_LISTS } from '~/utils/constants'
import { scrollIntoView } from '~/utils/index'

export default defineComponent({
  name: 'BaseShop',
  components: {
    BasePrice,
    BookCover,
    DesktopFilters,
    DesktopSorting,
    MobileFilterAndSort,
    StarsRating
  },
  props: {
    books: {
      type: Array,
      default: () => []
    },
    genres: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({})
    },
    isLoading: {
      type: Boolean,
      default: true
    },
    routeQuery: {
      type: Object,
      default: () => ({})
    }
  },
  setup() {
    const routeStore = useRouteStore()
    return { routeStore }
  },
  computed: {
    filters() {
      const allLabel = this.$label('page.shop.filters.all')

      return [{ id: 'all', label: allLabel }, ...this.genres]
    },
    currentPage() {
      return this.pagination.currentPageIndex
    },
    totalPages() {
      return this.pagination.totalNumberOfPages
    },
    sortBy() {
      return this.routeQuery.sort
    },
    selectedFilter() {
      return this.routeQuery.genre
    },
    prevPage() {
      return this.routeStore.from
    }
  },
  mounted() {
    try {
      if (this.prevPage && this.prevPage.name === 'shop-boek-slug') {
        setTimeout(() => this.scrollToBook(this.prevPage.params.slug), 50)
      }

      this.pushViewItemListEvent()
      // eslint-disable-next-line no-empty
    } catch {}
  },
  methods: {
    getTrackingItem(book) {
      return {
        ...book,
        name: book.title,
        discount: book.listPrice - book.salePrice,
        price: book.listPrice,
        ...TRACKING_LISTS.shop
      }
    },
    pushViewItemListEvent() {
      const products = this.books.map((book) => this.getTrackingItem(book))

      this.$gtmTracking.viewItemList({
        products,
        ...TRACKING_LISTS.shop
      })
    },
    pushSelectItemEvent(book) {
      try {
        this.$gtmTracking.selectItem({
          item: this.getTrackingItem(book),
          ...TRACKING_LISTS.shop
        })
        // eslint-disable-next-line no-empty
      } catch {}
    },
    routerPush(genre, sortBy, page) {
      let query = {}

      if (genre) query = { genre }
      if (sortBy) query.sort = sortBy
      if (page > 1) query.page = page

      this.$router.push({
        path: 'shop',
        query
      })
    },
    onSortChange(sortBy) {
      if (this.sortBy !== sortBy) {
        this.routerPush(this.selectedFilter, sortBy, 1)
      }
    },
    onFilterClick(filter) {
      if (this.selectedFilter !== filter) {
        this.routerPush(filter, this.sortBy, 1)
      }
    },
    pageChanged(page) {
      this.routerPush(this.selectedFilter, this.sortBy, page)
    },
    isDuplicateFilter(genre, filters) {
      return filters.some((filter) => filter.label === genre)
    },
    scrollToBook(slug) {
      try {
        const elements = this.$refs[slug]

        if (elements && elements[0] && window.scrollY === 0) {
          scrollIntoView(elements[0])
        }
        // eslint-disable-next-line no-empty
      } catch {}
    }
  }
})
</script>

<style lang="scss" scoped>
.fade-light-enter-active,
.fade-light-leave-active {
  transition: all 0.3s ease-out;
  opacity: 0;
}

.loading {
  opacity: 0.2;
  transform-origin: 50% 50%;
}
</style>
